import { useQuery } from "@apollo/client";
import { Alert, Box, Typography } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { ReactElement, useCallback } from "react";

import {
  CreateSuppressionsFromShortcodeSubscribersMutation,
  CreateSuppressionsFromShortcodeSubscribersMutationVariables,
  GetSubaccountSubscriberAudienceQuery,
  GetSubaccountSubscriberAudienceQueryVariables,
  GetSubscriberSummaryQuery,
  GetSubscriberSummaryQueryVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import TooltipButton from "~frontend/components/TooltipButton";
import { CREATE_SUPPRESSIONS_FROM_SHORTCODE_SUBSCRIBERS } from "~frontend/graphql/campaigns";
import {
  GET_SUBACCOUNT_SUBSCRIBER_AUDIENCE,
  GET_SUBSCRIBER_SUMMARY,
} from "~frontend/graphql/shortcode";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { CampaignWithSuppressionData } from "~frontend/routes/types";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import { plural } from "~frontend/util/plural";
import useMutation from "~frontend/util/useMutation";

import { isSendInProgress } from "./sendInProgress";
import { isSuppressionJobRunning } from "./suppressionJobs";

export function SuppressShortcodeSubscribers({
  campaign,
}: {
  campaign: CampaignWithSuppressionData;
}): ReactElement {
  const { subaccount } = useSubaccount()!;
  const errorMessage = useErrorMessageFormatter();
  const confirm = useConfirm();

  const subscriberAudienceQuery = useQuery<
    GetSubaccountSubscriberAudienceQuery,
    GetSubaccountSubscriberAudienceQueryVariables
  >(GET_SUBACCOUNT_SUBSCRIBER_AUDIENCE, {
    variables: {
      input: {
        subaccountId: subaccount.id,
      },
    },
  });

  const subscriberAudienceId =
    subscriberAudienceQuery.data?.getSubaccount.subscriberAudience?.id;

  const summaryQuery = useQuery<
    GetSubscriberSummaryQuery,
    GetSubscriberSummaryQueryVariables
  >(GET_SUBSCRIBER_SUMMARY, {
    variables: {
      input: {
        audienceId: subscriberAudienceId ?? "",
      },
    },
    skip: !subscriberAudienceId,
    fetchPolicy: "network-only",
  });

  const [startSuppressionJob, { loading: startingSuppressionJob }] =
    useMutation<
      CreateSuppressionsFromShortcodeSubscribersMutation,
      CreateSuppressionsFromShortcodeSubscribersMutationVariables
    >(CREATE_SUPPRESSIONS_FROM_SHORTCODE_SUBSCRIBERS, {
      onErrorSnackbarMessage: "Error starting suppression",
    });

  const handleStartSuppressionJob = useCallback(async () => {
    if (!subscriberAudienceId) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Suppress Short Code Subscribers",
      description:
        "This will suppress all short code subscribers from this campaign.",
    });
    if (!confirmed) {
      return;
    }

    await startSuppressionJob({
      variables: {
        input: { campaignId: campaign.id, audienceId: subscriberAudienceId },
      },
    });
  }, [subscriberAudienceId, confirm, startSuppressionJob, campaign]);

  const subscriberCount =
    summaryQuery.data?.getSubscriberSummary.activeSubscribers;

  if (!subscriberCount) {
    return <></>;
  }

  if (subscriberAudienceQuery.error || summaryQuery.error) {
    return (
      <Alert severity="error">
        Error fetching subscribers:{" "}
        {errorMessage(subscriberAudienceQuery.error ?? summaryQuery.error)}
      </Alert>
    );
  }

  const sendInProgress = isSendInProgress(campaign);

  const alreadySuppressed = campaign.shortcodeSubscriberSuppressions.length > 0;

  return (
    <Box sx={{ backgroundColor: palette.lightestGray, padding: "24px 36px" }}>
      <FlexBox justifyContent="space-between" direction="row">
        <Box>
          <Typography variant="body1Bold">Subscribers</Typography>
          <Typography variant="body1">
            {plural(subscriberCount, "Active Subscriber", "Active Subscribers")}
          </Typography>
        </Box>
        <TooltipButton
          variant="contained"
          onClick={handleStartSuppressionJob}
          disabled={
            sendInProgress ||
            startingSuppressionJob ||
            alreadySuppressed ||
            isSuppressionJobRunning(campaign)
          }
          tooltipText={
            sendInProgress
              ? "Cannot suppress contacts while sending is in progress"
              : undefined
          }
        >
          Suppress All Subscribers
        </TooltipButton>
      </FlexBox>
    </Box>
  );
}
